/**
 * Iter 83.2B-Polish — @-autocomplete primitives.
 *
 * Exports:
 *   <MentionInput as="input"|"textarea" value={...} onChange={(v) => ...} ... />
 *     Drop-in replacement for native `<input>` / `<textarea>` that
 *     surfaces an `@user` dropdown when the user types `@token`.
 *   useMentionAutocomplete + MentionDropdown — for advanced use.
 *
 * Tenant-scoping, empty-prefix suppression, and ownership enforcement
 * happen at the backend endpoint.
 */
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AtSign } from "lucide-react";
import { api } from "@/lib/api";

const MAX_ITEMS = 6;

export function useMentionAutocomplete({ textareaRef, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [anchor, setAnchor] = useState({ top: 0, left: 0 });
  const debounceRef = useRef(null);

  const detect = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return null;
    const pos = el.selectionStart;
    const text = value || "";
    const upto = text.slice(0, pos);
    const m = /(^|\s)@([A-Za-z0-9._-]*)$/.exec(upto);
    if (!m) return null;
    return { token: m[2], at: pos - (m[2].length + 1), end: pos };
  }, [textareaRef, value]);

  const computeAnchor = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return { top: 0, left: 0 };
    const rect = el.getBoundingClientRect();
    return { top: rect.bottom + 4, left: rect.left };
  }, [textareaRef]);

  const triggerFetch = useCallback((token) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!token || token.length < 1) {
      setItems([]); setOpen(false); return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/users/autocomplete", {
          params: { handle_prefix: token, limit: MAX_ITEMS },
        });
        setItems(Array.isArray(data) ? data.slice(0, MAX_ITEMS) : []);
        setActiveIdx(0);
        setOpen((Array.isArray(data) && data.length > 0));
      } catch {
        setItems([]); setOpen(false);
      }
    }, 120);
  }, []);

  useEffect(() => {
    const m = detect();
    if (!m) { setOpen(false); return; }
    setAnchor(computeAnchor());
    triggerFetch(m.token);
  }, [value, detect, computeAnchor, triggerFetch]);

  const select = useCallback((idx) => {
    const m = detect();
    if (!m) { setOpen(false); return; }
    const item = items[idx];
    if (!item || !item.handle) { setOpen(false); return; }
    const text = value || "";
    const next = text.slice(0, m.at) + "@" + item.handle + " " + text.slice(m.end);
    onChange(next);
    setOpen(false); setItems([]);
    setTimeout(() => {
      const el = textareaRef.current;
      if (!el) return;
      const newCaret = m.at + item.handle.length + 2;
      el.focus();
      el.setSelectionRange(newCaret, newCaret);
    }, 0);
  }, [detect, items, value, onChange, textareaRef]);

  const onKeyDown = useCallback((e) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault(); setActiveIdx((i) => (i + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault(); setActiveIdx((i) => (i - 1 + items.length) % items.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault(); select(activeIdx);
    } else if (e.key === "Escape") {
      e.preventDefault(); setOpen(false);
    }
  }, [open, items, activeIdx, select]);

  return { open, items, activeIdx, setActiveIdx, anchor, select, onKeyDown };
}


export function MentionDropdown({ state }) {
  if (!state.open || state.items.length === 0) return null;
  return (
    <div
      className="fixed z-50 fop-surface-2 rounded-md shadow-2xl
                    border border-[var(--fop-border)] py-1 min-w-[240px]"
      style={{ top: state.anchor.top, left: state.anchor.left }}
      data-testid="mention-autocomplete"
    >
      <div className="px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em]
                        text-[var(--fop-text-4)] border-b border-[var(--fop-border)] mb-1">
        Mention
      </div>
      {state.items.map((u, i) => (
        <button
          key={u.id}
          type="button"
          onMouseDown={(e) => { e.preventDefault(); state.select(i); }}
          onMouseEnter={() => state.setActiveIdx(i)}
          className={`w-full text-left px-3 py-2 flex items-center gap-2 min-h-[40px]
                        transition-colors
                        ${i === state.activeIdx
                           ? "bg-[var(--fop-primary,#ff6a1a)]/15 text-[var(--fop-text)]"
                           : "text-[var(--fop-text-2)] hover:bg-[var(--fop-surface)]"}`}
          data-testid={`mention-suggest-${u.handle}`}
        >
          <span className="w-6 h-6 rounded-full bg-[var(--fop-surface)]
                              flex items-center justify-center text-[var(--fop-text-3)]">
            <AtSign size={12} />
          </span>
          <span className="flex-1 min-w-0">
            <span className="text-sm font-medium" style={{ color: "var(--fop-primary, #ff6a1a)" }}>
              @{u.handle}
            </span>
            <span className="text-xs text-[var(--fop-text-3)] ml-2">
              {u.name}
            </span>
          </span>
          <span className="text-[10px] font-mono uppercase tracking-wider
                              text-[var(--fop-text-4)]">
            {u.role}
          </span>
        </button>
      ))}
    </div>
  );
}


/**
 * MentionInput — drop-in <input> / <textarea> with @-autocomplete.
 * Props mirror native input props; value is required + controlled.
 */
export const MentionInput = forwardRef(function MentionInput(
  { as: As = "input", value, onChange, onKeyDown, className,
    ...rest }, ref) {
  const innerRef = useRef(null);
  // Expose either the inner ref or the forwarded ref.
  const setRef = useCallback((el) => {
    innerRef.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
  }, [ref]);
  const state = useMentionAutocomplete({
    textareaRef: innerRef, value, onChange,
  });
  const handleKeyDown = useCallback((e) => {
    state.onKeyDown(e);
    if (!e.defaultPrevented && onKeyDown) onKeyDown(e);
  }, [state, onKeyDown]);
  return (
    <>
      <As
        ref={setRef}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        className={className}
        {...rest}
      />
      <MentionDropdown state={state} />
    </>
  );
});
